import { useEffect, useState } from "react";
import Layout from "../../layout/builders/Layout";
import CardTile from "../../layout/builders/CardTile";
import ProjectTiles from "../../layout/builders/Dashboard/ProjectTile";
import Piechart from "../../layout/builders/Dashboard/Piechart";
import Barchart from "../../layout/builders/Dashboard/Barchart";
import calculateProfileCompletion from "../../layout/builders/ProfileDetails/calculateProfileCompletion";
import useProfileStore from "../../store/builders/useProfileStore";
import useDashboardStore from "../../store/builders/useDashboardStore";
import useBuilderStatusStore from "../../store/builders/useBuilderStatusStore";
import useServiceStore from "../../store/builders/useServiceStore";
import RankingDisplay from "../../layout/builders/Dashboard/RankingDisplay";
import useAuthStore from "../../store/useAuthStore";
import ScrollingInfoBanner from "../../components/ScrollingInfoBanner";
import { axiosInstance } from "../../lib/axiosInstance";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CreditCardIcon,
  CurrencyRupeeIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

/* -----------------------------------
   Responsive ProfileProgressCircle
------------------------------------- */
const ProfileProgressCircle = ({
  progress = 0,
  profileImageUrl = "/assets/Support.png",
}) => {
  const getSize = () => {
    if (typeof window !== "undefined") {
      if (window.innerWidth < 400) return 70;
      if (window.innerWidth < 640) return 90;
    }
    return 120;
  };

  const [size, setSize] = useState(getSize());

  useEffect(() => {
    const handleResize = () => setSize(getSize());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const strokeWidth = size <= 70 ? 5 : size <= 90 ? 6 : 7;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - progress / 100);

  return (
    <div
      className="flex flex-col items-center justify-center"
      style={{
        minWidth: size,
        minHeight: size,
      }}
    >
      <div
        className="relative flex items-center justify-center"
        style={{
          width: size,
          height: size,
        }}
      >
        <svg
          width={size}
          height={size}
          className="block"
          style={{ minWidth: size, minHeight: size }}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={progress >= 96 ? "#4ADE80" : "#EF4444"}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{
              transition: "stroke-dashoffset 0.6s cubic-bezier(.4,2,.6,1)",
            }}
          />
        </svg>

        <div
          className="absolute top-1/2 left-1/2 rounded-full overflow-hidden bg-white border border-gray-200"
          style={{
            width: size * 0.68,
            height: size * 0.68,
            transform: "translate(-50%, -50%)",
            boxShadow: "0 2px 8px 0 rgba(0,0,0,0.04)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={profileImageUrl}
            alt="Profile"
            className="object-cover w-full h-full"
            style={{
              minWidth: "100%",
              minHeight: "100%",
              borderRadius: "9999px",
            }}
            draggable={false}
          />
        </div>

        <div
          className="absolute left-1/2 bottom-0 translate-x-[-50%] translate-y-[60%] bg-white rounded-lg shadow px-2 py-1 text-xs font-semibold text-gray-800"
          style={{
            minWidth: size * 0.38,
            textAlign: "center",
            fontSize: size <= 70 ? 12 : size <= 90 ? 14 : 16,
          }}
        >
          {Math.round(progress)}%
        </div>
      </div>
    </div>
  );
};

/* -----------------------------------
   Builder Dashboard
------------------------------------- */
const Dashboard = () => {
  const { profile, fetchProfile } = useProfileStore();
  const { getCardTile, getProjectTile, getChartData, cardTile, projectTile } = useDashboardStore();
  const { builderServices, fetchBuilderService } = useServiceStore();
  const { user, token } = useAuthStore.getState();
  const navigate = useNavigate();

  const [profileCompletion, setProfileCompletion] = useState(0);
  const [portfolioItems, setPortfolioItems] = useState([]);

  const [rankingData, setRankingData] = useState({
    stateRank: null,
    stateTotal: null,
    districtRank: null,
    districtTotal: null,
    talukRank: null,
    talukTotal: null,
  });

  const { status: builderStatus, reason: holdReason, setStatus: setBuilderStatus, fetchBuilderStatus } = useBuilderStatusStore();
  const [isStatusApiLoading, setIsStatusApiLoading] = useState(true);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

  const [serviceAreaCost, setServiceAreaCost] = useState(null);
  const [isFetchingCost, setIsFetchingCost] = useState(false);
  const [costFetchError, setCostFetchError] = useState(null);

  /* ------------------------------
     FETCH BUILDER RANKING (Correct one)
  ------------------------------- */
  useEffect(() => {
    const fetchBuilderRanking = async () => {
      if (user && user.builderUniqueId) {
        try {
          const { axiosInstancev2 } = await import("../../lib/axiosInstance");
          const response = await axiosInstancev2.get(
            `/leaderboard/my-rank/${user.builderUniqueId}`
          );

          if (response.data.success) {
            setRankingData({
              stateRank: response.data.state?.rank || null,
              stateTotal: response.data.state?.total || null,
              districtRank: response.data.district?.rank || null,
              districtTotal: response.data.district?.total || null,
              talukRank: response.data.taluk?.rank || null,
              talukTotal: response.data.taluk?.total || null,
            });
          }
        } catch (error) {
          console.error("Error fetching builder ranking:", error);
        }
      }
    };

    fetchBuilderRanking();
  }, [user]);

  /* ------------------------------
     INITIAL LOAD (PROFILE + CHARTS)
  ------------------------------- */
  useEffect(() => {
    const loadInitialData = async () => {
      await fetchProfile();
      await getCardTile();
      await getProjectTile();
      await getChartData();
      await fetchBuilderService();
    };
    loadInitialData();
  }, [fetchProfile, getCardTile, getProjectTile, getChartData, fetchBuilderService]);

  /* ------------------------------
     PROFILE COMPLETION %
  ------------------------------- */
  useEffect(() => {
    if (profile) {
      const completionPercentage = calculateProfileCompletion(profile);
      setProfileCompletion(completionPercentage);
    }
  }, [profile]);

  /* ------------------------------
     FETCH PORTFOLIO ITEMS
  ------------------------------- */
  useEffect(() => {
    const fetchPortfolioItems = async () => {
      if (user && user.id && token) {
        try {
          const { axiosInstancev1 } = await import("../../lib/axiosInstance");
          const response = await axiosInstancev1.get(
            `/builder/${user.id}/portfolio`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          if (response.data && Array.isArray(response.data)) {
            setPortfolioItems(response.data);
          }
        } catch (error) {
          console.error("Error fetching portfolio items:", error);
        }
      }
    };

    fetchPortfolioItems();
  }, [user, token]);

  /* ------------------------------
     SERVICE AREA COST
  ------------------------------- */
  const fetchServiceAreaCost = async (builderId, authToken) => {
    setIsFetchingCost(true);
    setCostFetchError(null);
    setServiceAreaCost(null);

    try {
      const { axiosInstancev1 } = await import("../../lib/axiosInstance");
      const response = await axiosInstancev1.get(
        `/builder/${builderId}/service-area-details`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.success) {
        setServiceAreaCost(response.data.totalServiceAreaCost);
      } else {
        throw new Error(
          response.data.message || "Failed to fetch service area cost."
        );
      }
    } catch (error) {
      console.error("Error fetching service area cost:", error);
      const errorMsg =
        error.response?.data?.message || "Could not retrieve payment details.";
      setCostFetchError(errorMsg);
    } finally {
      setIsFetchingCost(false);
    }
  };

  /* ------------------------------
     BUILDER AUTH STATUS
  ------------------------------- */
  useEffect(() => {
    const fetchBuilderAuthStatus = async () => {
      if (user && user.builderUniqueId && token) {
        setIsStatusApiLoading(true);
        try {
          // Use the store's fetch method
          await fetchBuilderStatus();
          const status = useBuilderStatusStore.getState().status;

          if (status) {

            const modalStatuses = [
              "Pending_Verification",
              "Approved",
              "Rejected",
              "On_hold",
            ];

            if (modalStatuses.includes(status)) {
              setIsStatusModalOpen(true);

              if (status === "Approved" && user.id) {
                fetchServiceAreaCost(user.id, token);
              }
            } else if (status === "Active" || status === "Onboarded") {
              // Close modal for legacy "Onboarded" status or "Active" status
              setIsStatusModalOpen(false);
            }
          }
        } catch (error) {
          console.error("Error fetching builder status:", error);
          toast.error(
            "Could not verify account status. Please try logging in again."
          );
        } finally {
          setIsStatusApiLoading(false);
        }
      } else {
        setIsStatusApiLoading(false);
      }
    };

    fetchBuilderAuthStatus();
  }, [user, token, fetchBuilderStatus, builderStatus]);

  /* ------------------------------
     MODAL HELPERS
  ------------------------------- */
  const handleCloseDismissibleModal = () => {
    setIsStatusModalOpen(false);
  };

  const getModalContent = () => {
    const dismissButton = (
      <button
        onClick={handleCloseDismissibleModal}
        className="mt-6 px-6 py-2.5 bg-gray-200 text-gray-700 font-medium text-sm rounded-lg hover:bg-gray-300 transition-colors"
      >
        OK, I Understand
      </button>
    );

    const paymentDetailsSection = (
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-sm text-gray-600 mb-2">
          Your subscription fee to activate the account is:
        </p>

        {isFetchingCost ? (
          <div className="flex items-center justify-center">
            <AiOutlineLoading3Quarters className="animate-spin text-blue-500 mr-2" />
            <span className="text-gray-500 text-sm">Loading amount...</span>
          </div>
        ) : costFetchError ? (
          <p className="text-sm text-red-500 font-medium">{costFetchError}</p>
        ) : serviceAreaCost !== null ? (
          <div className="flex items-baseline justify-center">
            <CurrencyRupeeIcon className="w-6 h-6 text-green-600 mr-1" />
            <span className="text-3xl font-bold text-green-600">
              {Number(serviceAreaCost).toLocaleString("en-IN")}
            </span>
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">
            Could not load payment amount.
          </p>
        )}
      </div>
    );

    const proceedToPaymentButton = (
      <button
        onClick={() => navigate("/builder/subscription")}
        disabled={
          isFetchingCost || serviceAreaCost === null || !!costFetchError
        }
        className="w-full mt-6 px-6 py-2.5 bg-blue-500 text-white font-medium text-sm rounded-lg hover:bg-blue-600 transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Proceed to Payment
      </button>
    );

    switch (builderStatus) {
      case "Pending_Verification":
        return {
          icon: (
            <ShieldCheckIcon className="w-16 h-16 text-yellow-500 mx-auto mb-5" />
          ),
          title: "Verification Pending",
          message:
            "User created successfully. Your account is pending verification. You can use the dashboard once the admin approves your account.",
          isClosable: false,
          actions: null,
        };

      case "Approved":
        return {
          icon: (
            <CreditCardIcon className="w-16 h-16 text-green-500 mx-auto mb-5" />
          ),
          title: "Account Approved!",
          message:
            "Your account has been approved. Please complete the payment to activate your dashboard and access all features.",
          paymentInfo: paymentDetailsSection,
          isClosable: false,
          actions: proceedToPaymentButton,
        };

      case "Rejected":
        return {
          icon: (
            <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-5" />
          ),
          title: "Account Rejected",
          message: holdReason
            ? `Your account has been rejected. Reason: ${holdReason}`
            : "Your account has been rejected. You can no longer use this account.",
          additionalInfo: (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium">
                ⚠️ You are blocked from accessing this account
              </p>
              <p className="text-xs text-red-600 mt-1">
                Please contact support if you believe this is an error.
              </p>
            </div>
          ),
          isClosable: false,
          actions: (
            <button
              onClick={() => {
                const { logout } = useAuthStore.getState();
                logout();
                navigate("/login?type=builder");
              }}
              className="mt-6 px-6 py-2.5 bg-red-500 text-white font-medium text-sm rounded-lg hover:bg-red-600 transition-colors"
            >
              Logout
            </button>
          ),
        };

      case "On_hold":
        return {
          icon: (
            <InformationCircleIcon className="w-16 h-16 text-orange-500 mx-auto mb-5" />
          ),
          title: "Account On Hold",
          message: "Your account is currently on hold pending verification.",
          additionalInfo: holdReason && (
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-700 font-semibold mb-1">
                Reason:
              </p>
              <p className="text-sm text-orange-800">
                {holdReason}
              </p>
            </div>
          ),
          isClosable: false,
          actions: (
            <button
              onClick={() => {
                const { logout } = useAuthStore.getState();
                logout();
                navigate("/login?type=builder");
              }}
              className="mt-6 px-6 py-2.5 bg-gray-500 text-white font-medium text-sm rounded-lg hover:bg-gray-600 transition-colors"
            >
              Logout
            </button>
          ),
        };

      default:
        return null;
    }
  };

  const modalContent = getModalContent();
  const showBlurOverlay = isStatusModalOpen && modalContent;

  /* ------------------------------
     RENDER
  ------------------------------- */
  return (
    <div className="min-h-screen bg-gray-100">
      <Layout builderStatus={builderStatus}>
        {isStatusApiLoading && !showBlurOverlay && (
          <div className="fixed inset-0 z-[101] flex items-center justify-center bg-white/50">
            <AiOutlineLoading3Quarters className="animate-spin text-3xl text-blue-600" />
            <p className="ml-3 text-gray-700">Checking account status...</p>
          </div>
        )}

        <AnimatePresence>
          {showBlurOverlay && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
              onClick={
                modalContent.isClosable
                  ? handleCloseDismissibleModal
                  : undefined
              }
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.8, opacity: 0, y: 20 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 max-w-lg w-full text-center"
                onClick={(e) => e.stopPropagation()}
              >
                {modalContent.icon}
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3">
                  {modalContent.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-2 leading-relaxed">
                  {modalContent.message}
                </p>
                {modalContent.additionalInfo}
                {modalContent.paymentInfo}
                {modalContent.actions}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={showBlurOverlay ? "blur-sm pointer-events-none" : ""}>
          <ScrollingInfoBanner
            services={builderServices}
            portfolioItems={portfolioItems}
            profileCompletion={profileCompletion}
          />

          {/* Top Section: Card, Ranking, Profile - Responsive Grid */}
          <div className="px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {/* Card Tile */}
              <div className="w-full flex justify-center md:justify-start xl:justify-center">
                <CardTile />
              </div>

              {/* Ranking Display */}
              <div className="w-full flex justify-center md:justify-end xl:justify-center">
                <RankingDisplay rankingData={rankingData} />
              </div>

              {/* Profile Progress - Full width on mobile, centered on larger screens */}
              <div className="w-full flex justify-center md:col-span-2 xl:col-span-1">
                <ProfileProgressCircle
                  progress={profileCompletion}
                  profileImageUrl={
                    profile?.builder?.profile_image || "/assets/Support.png"
                  }
                />
              </div>
            </div>
          </div>

          {/* Project Tiles Section */}
          <div className="px-3 sm:px-4 lg:px-6 pb-4 sm:pb-6">
            <ProjectTiles />
          </div>

          {/* Charts Section - Responsive Layout */}
          <div className="px-3 sm:px-4 lg:px-6 pb-20 sm:pb-6 mb-4 sm:mb-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Bar Chart - Takes 2 columns on large screens */}
              <div className="lg:col-span-2">
                <Barchart />
              </div>
              {/* Pie Chart - Takes 1 column on large screens */}
              <div className="lg:col-span-1">
                <Piechart />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default Dashboard;
